import { useMemo } from 'react';
import { Box, Typography, Tooltip, CircularProgress } from '@mui/material';
import styled from 'styled-components';
import SimpleCalendar from 'components/SimpleCalendar';
import { renderIntegrationHealthByRequestAndSuccessCountPercent } from 'utils';
import { TargetService, integrationsHash } from '@constants';
import { IntegrationHealth } from 'types';
import {
  useOverviewAggregateHealthQuery,
  AggregateHealthDay,
} from './useOverviewAggregateHealthQuery';

// Build the IntegrationHealth shape the health-icon helper expects from one
// aggregated day. id is synthetic (no underlying DB row to point at) and
// serviceName is the currently selected service so any service-scoped
// downstream display reads correctly.
const toSyntheticHealth = (
  day: AggregateHealthDay,
  targetService: TargetService,
): IntegrationHealth => ({
  id: 0,
  date: day.date,
  serviceName: targetService as IntegrationHealth['serviceName'],
  requestCount: day.requestCount,
  successCount: day.successCount,
  errors: day.errors as IntegrationHealth['errors'],
});

const CalendarStyler = styled.div`
  .fc-daygrid-event {
    display: flex;
    justify-content: center;
    background-color: transparent !important;
  }
`;

/**
 * 30-day aggregate health calendar for the overview page. One cell per date,
 * showing the rolled-up daily health icon across all teams using the selected
 * integration. Data is fetched and aggregated client-side via
 * `useOverviewAggregateHealthQuery` (no new IS endpoint).
 */
const OverviewAggregateHealthCalendar = ({
  targetService,
  targetServiceIds,
}: {
  targetService: TargetService;
  targetServiceIds: number[];
}) => {
  const { days, isLoading } = useOverviewAggregateHealthQuery({
    targetService,
    targetServiceIds,
    days: 30,
  });

  const events = useMemo(
    () =>
      days.map((day: AggregateHealthDay) => ({
        id: day.date,
        key: day.date,
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        start: day.date,
        extendedProps: { day },
      })),
    [days],
  );

  const renderHeader = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 2,
        pb: 0,
      }}
    >
      <Typography variant="h6">
        Aggregate Health — {integrationsHash[targetService]?.label ?? targetService}
      </Typography>
      {isLoading ? <CircularProgress size={16} /> : null}
    </Box>
  );

  return (
    <CalendarStyler>
      <SimpleCalendar
        events={events}
        renderHeader={renderHeader}
        eventContent={(arg) => {
          const { day } = arg.event.extendedProps as { day: AggregateHealthDay };
          const tooltipBody = (
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                }}
              >
                {day.date}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                }}
              >
                {day.successCount.toLocaleString()} / {day.requestCount.toLocaleString()} succeeded
                ({day.successRate.toFixed(1)}%)
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                }}
              >
                Active integrations: {day.integrationCount}
              </Typography>
            </Box>
          );
          return (
            <Tooltip title={tooltipBody}>
              <span>
                {renderIntegrationHealthByRequestAndSuccessCountPercent({
                  integrationHealth: toSyntheticHealth(day, targetService),
                  className: 'icon-only no-margin-on-icon',
                })}
              </span>
            </Tooltip>
          );
        }}
      />
    </CalendarStyler>
  );
};

export default OverviewAggregateHealthCalendar;
